<template>
  <transition name="slideInRight">
    <div v-if="isOpen" class="admin-dashboard" @click.stop>
      <!-- Top container for save area -->
      <div
        v-if="activeTab === 'permissions' || activeTab === 'contentVisibility'"
        class="save-container"
      >
        <div class="save-content-wrapper">
          <transition name="fade-swap" mode="out-in">
            <!-- Save Changes confirmation -->
            <div
              v-if="showCloseConfirm"
              key="confirm"
              class="save-confirmation"
            >
              <span
                class="save-text"
                :class="{ 'highlight-attention': shouldHighlight }"
                >Save changes?</span
              >
              <button @click="confirmSave" class="confirm-button">Yes</button>
              <button @click="discardChanges" class="confirm-button">No</button>
            </div>

            <!-- Normal Save Changes button -->
            <div v-else key="save" class="save-normal">
              <button
                v-if="changesMade"
                class="save-button smallButton"
                @click="saveChanges"
                :disabled="processingChanges"
              >
                <span v-if="processingChanges">Processing...</span>
                <span v-else>Save changes</span>
              </button>
              <transition name="fade">
                <!-- Add this transition wrapper -->
                <div
                  v-if="saveMessage"
                  class="save-message"
                  :class="{
                    'save-success': isSaveSuccessful,
                    'save-error': !isSaveSuccessful,
                  }"
                >
                  {{ saveMessage }}
                </div>
              </transition>
            </div>
          </transition>
        </div>
      </div>

      <!-- Main content area -->
      <div class="admin-dashboard-body">
        <!-- Tab Navigation -->
        <div class="tab-navigation">
          <h4
            @click="activeTab = 'permissions'"
            :class="{ active: activeTab === 'permissions' }"
            class="tab-button"
          >
            Manage User Permissions
          </h4>
          <h4
            @click="activeTab = 'contentVisibility'"
            :class="{ active: activeTab === 'contentVisibility' }"
            class="tab-button"
          >
            Content Visibility
          </h4>
        </div>

        <!-- Permissions Tab -->
        <div v-if="activeTab === 'permissions'">
          <div v-if="loadingUsers" class="loading-message">
            Loading users...
          </div>
          <table v-else class="user-table">
            <thead>
              <tr>
                <th>User</th>

                <th>Privileges</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(user, index) in filteredUsers" :key="user.uid">
                <td>
                  <div class="row">
                    <input
                      class="smallInput borderBottomInput noPaddingLeft"
                      v-model="user.editName"
                      @input="markChanges"
                      placeholder="Add name"
                    />
                    <span
                      v-if="user.confidentiality_agreement_signed"
                      class="agreement-indicator-wrapper"
                      title="User accepted the initial Terms & Conditions."
                    >
                      <svg class="agreement-indicator">
                        <use
                          href="@/assets/iconset.svg#signed_contract_compact"
                        ></use>
                      </svg>
                    </span>
                  </div>
                  <div class="row alignCentreRow">
                    <small class="grey-dark"
                      >{{ user.email }}
                      <span v-if="formatDate(user.last_logged_in) !== 'Never'">
                        | Last logged:

                        {{ formatDate(user.last_logged_in) }}
                      </span></small
                    >
                  </div>
                </td>

                <td>
                  <select
                    v-model="user.editPrivileges"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitor"
                      title="Visitor: Basic access to public content only"
                    >
                      Visitor
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidate: Access to investor-focused content, has not yet signed confidentiality agreement"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investor: Full investor access, has signed confidentiality agreement"
                    >
                      Conf. Inv.
                    </option>
                    <option
                      value="admin"
                      title="Administrator: Full access to all content and user management"
                    >
                      Admin
                    </option>
                  </select>
                  <div class="delete-row">
                    <button
                      class="delete-user-button"
                      @click="openDeleteConfirm(user)"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <ConfirmationDialog
            :show="!!confirmUid"
            :message="confirmMessage"
            confirmText="Delete"
            cancelText="Cancel"
            :processing="processingDelete"
            @confirm="confirmDelete"
            @cancel="closeConfirm"
          />
        </div>

        <!-- Content Visibility Tab -->
        <div v-else-if="activeTab === 'contentVisibility'">
          <div v-if="loadingContentSettings" class="loading-message">
            Loading content settings...
          </div>
          <table v-else class="visibility-table">
            <thead>
              <tr>
                <th>Content</th>
                <th>Visibile to</th>
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              <!-- The 10 Year Plan Characters -->
              <tr>
                <td>10 Year Plan: Cecilia</td>
                <td>
                  <select
                    v-model="contentSettings['10YearPlan-Cecilia'].visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings['10YearPlan-Cecilia'].enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>10 Year Plan: Patricia</td>
                <td>
                  <select
                    v-model="contentSettings['10YearPlan-Patricia'].visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings['10YearPlan-Patricia'].enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>10 Year Plan: The Path</td>
                <td>
                  <select
                    v-model="contentSettings['10YearPlan-thePath'].visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings['10YearPlan-thePath'].enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>

              <!-- Business Model Sections - ordered as they appear on webpage -->
              <tr>
                <td>The Stories & Executive Production</td>
                <td>
                  <select
                    v-model="contentSettings.theFilmProjects.visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings.theFilmProjects.enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>The Characters & Immersive Marketing</td>
                <td>
                  <select
                    v-model="contentSettings.theCharacters.visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings.theCharacters.enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>The Artefacts</td>
                <td>
                  <select
                    v-model="contentSettings.theArtefacts.visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings.theArtefacts.enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>The App</td>
                <td>
                  <select
                    v-model="contentSettings.theApp.visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings.theApp.enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>The Documentary</td>
                <td>
                  <select
                    v-model="contentSettings.theDocumentary.visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings.theDocumentary.enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>Cultural & Institutional Partnerships</td>
                <td>
                  <select
                    v-model="contentSettings.thePartnerships.visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings.thePartnerships.enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>The Source Helix Creative System™</td>
                <td>
                  <select
                    v-model="contentSettings.theHelix.visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    v-model="contentSettings.theHelix.enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
              <tr>
                <td>Epilogue</td>
                <td>
                  <select
                    v-model="contentSettings.epilogue.visibility"
                    @change="markChanges"
                    class="smallSelect"
                  >
                    <option
                      value="visitors"
                      title="Visitors: Visible to everyone including anonymous users"
                    >
                      Visitors
                    </option>
                    <option
                      value="IC"
                      title="Investor Candidates: Visible to IC, CI, and Admin roles"
                    >
                      Inv. Cand.
                    </option>
                    <option
                      value="CI"
                      title="Confirmed Investors: Visible only to CI and Admin roles"
                    >
                      Conf. Inv.
                    </option>
                  </select>
                </td>
                <td class="text-right">
                  <input
                    type="checkbox"
                    v-model="contentSettings.epilogue.enabled"
                    @change="markChanges"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "@/firebase";
import ConfirmationDialog from "./ConfirmationDialog.vue";
import { adminDeleteUserByUid } from "@/firebase/useDeleteUser";
import { toast } from "vue3-toastify";

export default {
  name: "AdminDashboard",
  components: { ConfirmationDialog },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    closeRequestCount: {
      type: Number,
      default: 0,
    },
    currentUserId: {
      type: String,
      default: "",
    },
  },
  emits: ["closeDashboard"],
  setup(props, { emit }) {
    // activeTab: either "permissions" or "contentVisibility"
    const activeTab = ref("permissions");

    // All users fetched from Firestore.
    const users = ref([]);
    const loadingUsers = ref(true);
    // We'll keep a "backup" copy to revert if user discards changes.
    let originalUsers = [];

    // Content visibility settings
    const contentSettings = ref({
      "10YearPlan-Cecilia": { enabled: true, visibility: "CI" },
      "10YearPlan-Patricia": { enabled: true, visibility: "CI" },
      "10YearPlan-thePath": { enabled: true, visibility: "CI" },
      theArtefacts: { enabled: true, visibility: "visitors" },
      theApp: { enabled: true, visibility: "visitors" },
      theFilmProjects: { enabled: true, visibility: "visitors" },
      theCharacters: { enabled: true, visibility: "visitors" },
      theDocumentary: { enabled: true, visibility: "visitors" },
      thePartnerships: { enabled: true, visibility: "visitors" },
      theHelix: { enabled: true, visibility: "CI" },
      epilogue: { enabled: true, visibility: "CI" },
    });
    const loadingContentSettings = ref(true);
    let originalContentSettings = {};

    // For controlling the "Save Changes" button
    const changesMade = ref(false);
    const processingChanges = ref(false);

    // For success/error messages
    const saveMessage = ref("");
    const isSaveSuccessful = ref(false);

    // If user tries closing with unsaved changes, show "Save changes? yes/no"
    const showCloseConfirm = ref(false);
    const shouldHighlight = ref(false);

    // Fetch content visibility settings from Firestore
    async function fetchContentSettings() {
      try {
        const settings = {
          "10YearPlan-Cecilia": { enabled: true, visibility: "CI" },
          "10YearPlan-Patricia": { enabled: true, visibility: "CI" },
          "10YearPlan-thePath": { enabled: true, visibility: "CI" },
          theArtefacts: { enabled: true, visibility: "visitors" },
          theApp: { enabled: true, visibility: "visitors" },
          theFilmProjects: { enabled: true, visibility: "visitors" },
          theCharacters: { enabled: true, visibility: "visitors" },
          theDocumentary: { enabled: true, visibility: "visitors" },
          thePartnerships: { enabled: true, visibility: "visitors" },
          theHelix: { enabled: true, visibility: "CI" },
          epilogue: { enabled: true, visibility: "CI" },
        };

        // Fetch each content setting from Firestore
        const contentIds = Object.keys(settings);
        for (const contentId of contentIds) {
          const docRef = doc(db, "content_settings", contentId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            settings[contentId] = docSnap.data();
          }
        }

        // Update the content settings
        contentSettings.value = settings;

        // Create a backup copy
        originalContentSettings = JSON.parse(JSON.stringify(settings));

      } catch (error) {
        // Keep default settings if there's an error
        originalContentSettings = JSON.parse(
          JSON.stringify(contentSettings.value)
        );
      } finally {
        loadingContentSettings.value = false;
      }
    }

    // fetchUsers from Firestore + store a backup.
    async function fetchUsers() {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const temp = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          temp.push({
            uid: docSnap.id,
            email: data.email || "",
            editName: data.name || "",
            editPrivileges: data.privileges || "visitor",
            confidentiality_agreement_signed:
              data.confidentiality_agreement_signed || false,
            confidentiality_agreement_signed_at:
              data.confidentiality_agreement_signed_at || null,
            last_logged_in: data.last_logged_in
              ? data.last_logged_in.toDate()
              : null,
          });
        });
        users.value = temp;
        // Create a backup copy we can revert to if user discards changes
        originalUsers = JSON.parse(JSON.stringify(temp));
      } catch (error) {
      } finally {
        loadingUsers.value = false;
      }
    }

    // Filter out the admin themself from the list
    const filteredUsers = computed(() =>
      users.value.filter((u) => u.uid !== props.currentUserId)
    );

    // Called whenever user edits anything => "Save Changes" button appears
    function markChanges() {
      changesMade.value = true;
      saveMessage.value = "";
    }

    // Delete user confirmation state
    const confirmUid = ref("");
    const confirmMessage = ref("");
    const processingDelete = ref(false);

    function openDeleteConfirm(user) {
      confirmUid.value = user.uid;
      const label = user.editName || user.email || user.uid;
      confirmMessage.value = `Delete user \"${label}\"? This will permanently remove their account and data.`;
    }

    function closeConfirm() {
      if (!processingDelete.value) {
        confirmUid.value = "";
        confirmMessage.value = "";
      }
    }

    async function confirmDelete() {
      if (!confirmUid.value) return;
      processingDelete.value = true;
      const target = confirmUid.value;
      const result = await adminDeleteUserByUid(target);
      processingDelete.value = false;
      if (result.ok) {
        // Remove from local state
        users.value = users.value.filter((u) => u.uid !== target);
        toast("User deleted successfully.");
        confirmUid.value = "";
        confirmMessage.value = "";
      } else {
        if (result.code === "last-admin") {
          toast("Cannot delete the last admin account.");
        } else {
          toast(result.message || "Failed to delete user.");
        }
        // Keep dialog open to allow cancel
      }
    }

    // Save changes => update Firestore
    async function saveChanges() {
      processingChanges.value = true;
      try {
        if (activeTab.value === "permissions") {
          // Save user permissions
          for (const user of filteredUsers.value) {
            const userRef = doc(db, "users", user.uid);
            await updateDoc(userRef, {
              name: user.editName,
              privileges: user.editPrivileges,
            });
          }
          originalUsers = JSON.parse(JSON.stringify(users.value));
        } else if (activeTab.value === "contentVisibility") {
          // Save content visibility settings directly to Firestore
          const contentIds = Object.keys(contentSettings.value);
          for (const contentId of contentIds) {
            const docRef = doc(db, "content_settings", contentId);
            await setDoc(docRef, contentSettings.value[contentId]);
          }

          originalContentSettings = JSON.parse(
            JSON.stringify(contentSettings.value)
          );
        }

        saveMessage.value = "Changes saved successfully!";
        isSaveSuccessful.value = true;
        changesMade.value = false;

        if (showCloseConfirm.value) {
          showCloseConfirm.value = false;
          emit("closeDashboard");
        }
      } catch (error) {
        saveMessage.value = "Failed to save changes. Please try again.";
        isSaveSuccessful.value = false;
      } finally {
        processingChanges.value = false;
        // Clear message after 3 seconds
        setTimeout(() => {
          saveMessage.value = "";
        }, 3000);
      }
    }

    // Watch parent's closeRequestCount => attempt close
    watch(
      () => props.closeRequestCount,
      (newVal, oldVal) => {
        if (oldVal === newVal) return; // no change
        attemptClose();
      }
    );

    // Watch isOpen to reset messages when dashboard closes
    watch(
      () => props.isOpen,
      (newVal) => {
        if (!newVal) {
          saveMessage.value = "";
          showCloseConfirm.value = false;
        }
      }
    );

    // Watch for tab changes and prompt to save if there are unsaved changes
    watch(activeTab, (newTab, oldTab) => {
      if (oldTab && changesMade.value && !showCloseConfirm.value) {
        // User is switching tabs with unsaved changes
        showCloseConfirm.value = true;
        // Revert the tab change until they decide
        activeTab.value = oldTab;
      }
    });

    // If no unsaved changes => close. Else => show confirm inside save-container
    function attemptClose() {
      if (!changesMade.value) {
        emit("closeDashboard");
      } else {
        if (showCloseConfirm.value) {
          // User tried to close again while confirm is showing - highlight the text
          shouldHighlight.value = true;
          setTimeout(() => {
            shouldHighlight.value = false;
          }, 500);
        } else {
          // First time - just show the confirm
          showCloseConfirm.value = true;
        }
      }
    }

    // "Yes" => do saveChanges => that will close or allow tab switch
    function confirmSave() {
      showCloseConfirm.value = false;
      saveChanges();
    }

    // "No" => revert local changes from the backup, close
    function discardChanges() {
      changesMade.value = false;
      showCloseConfirm.value = false;
      saveMessage.value = "";

      if (activeTab.value === "permissions") {
        // Revert user data from original
        users.value = JSON.parse(JSON.stringify(originalUsers));
      } else if (activeTab.value === "contentVisibility") {
        // Revert content settings from original
        contentSettings.value = JSON.parse(
          JSON.stringify(originalContentSettings)
        );
      }

      // Then close
      emit("closeDashboard");
    }

    onMounted(() => {
      fetchUsers();
      fetchContentSettings();
    });

    // Format date for display
    function formatDate(date) {
      if (!date) return "Never";
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      });
    }

    return {
      activeTab,
      users,
      loadingUsers,
      filteredUsers,
      contentSettings,
      loadingContentSettings,
      changesMade,
      processingChanges,
      saveMessage,
      showCloseConfirm,
      shouldHighlight,

      fetchUsers,
      fetchContentSettings,
      markChanges,
      saveChanges,
      confirmSave,
      discardChanges,
      attemptClose,
      isSaveSuccessful,
      formatDate,
      openDeleteConfirm,
      closeConfirm,
      confirmDelete,
      confirmUid,
      confirmMessage,
      processingDelete,
    };
  },
};
</script>

<style scoped>
/* SVG icon indicator for signed micro-NDA */
.agreement-indicator-wrapper {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
}
.agreement-indicator {
  width: 20px;
  height: 20px;
  --icon-stroke-color: var(--grey-dark);
}
.admin-dashboard {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  min-width: 50vw;
  background-color: var(--bg-main-dark);
  border-left: 1px solid var(--border-light);
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.5);
  z-index: 6;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
}

/* Slide from right in/out */
.slideInRight-enter-active {
  animation: slideInFromRight 0.5s forwards ease;
}
.slideInRight-leave-active {
  animation: slideOutToRight 0.5s forwards ease;
}
@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
@keyframes slideOutToRight {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}

/* On mobile, widen 1px so border-left is off-screen */
@media (max-width: 768px) {
  .admin-dashboard {
    min-width: calc(100vw + 1px);
  }
}

/* The top area for saving or close confirm */
.save-container {
  height: var(--clickableHeight);
  padding: 0 var(--largeSpace);
  background-color: var(--bg-main-dark);
  border-bottom: 1px solid var(--border-light);
  position: relative;
}

.save-content-wrapper {
  position: relative;
  height: 100%;
}

.save-normal,
.save-confirmation {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: var(--space);
}

/* Updated transition styles */
.fade-swap-enter-active,
.fade-swap-leave-active {
  transition: all 0.4s ease;
}

/* Normal Save button area */
.save-button {
  border: 1px solid transparent;
  padding: 0;
  font-size: var(--smallFontSize);
  color: var(--purple-dark-tint-4);
  cursor: pointer;
  transition: color 0.3s ease, opacity 0.3s ease;
}
.save-button:hover:not(:disabled) {
  color: var(--purple-dark-tint-3);
  background-color: transparent;
}
.save-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* The top 'save changes?' confirmation */
.save-confirmation {
  display: flex;
  align-items: baseline;
  gap: var(--smallSpace);
}
.save-message,
.save-text {
  font-size: var(--regularFontSize);
  color: var(--text-white);
  transition: color 0.3s ease;
}

.save-text.highlight-attention {
  color: var(--red);
}

.save-message {
  transition: opacity 0.3s ease;
}

.save-message.save-success {
  color: var(--green);
}

.save-message.save-error {
  color: var(--red);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.confirm-button {
  padding: var(--smallSpace) var(--space);
  border: none;
  border-radius: var(--border-radius-pill);
  font-size: var(--smallFontSize);
  cursor: pointer;
  color: var(--purple-dark-tint-4);
  background-color: transparent;
  transition: color 0.3s ease;
}
.confirm-button:hover {
  color: var(--purple-dark-tint-2);
}

/* The main body for user table, etc. */
.admin-dashboard-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--largeSpace);
}

/* Loading message */
.loading-message {
  padding: var(--largeSpace) 0;
  color: var(--text-white);
  opacity: 0.6;
  font-size: var(--regularFontSize);
}

/* The table */
.user-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: var(--space);
}
.user-table thead th {
  text-align: left;
  border-bottom: 1px solid var(--border-light);
  padding: var(--space) 0;
  font-size: var(--smallFontSize);
}
.user-table td {
  text-align: left;
  border-bottom: 1px solid var(--grey-darkest);
  padding: var(--space) 0;
}
.user-table input,
.user-table select {
  border: 1px solid var(--grey);
  border-radius: var(--border-radius-pill);
  background: var(--bg-main-dark);
  color: var(--text-white);
  margin-bottom: 1px;
  font-size: var(--smallFontSize);
}
.borderBottomInput {
  border-bottom: 1px solid transparent !important;
  border-top: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
  text-align: start !important;
  border-radius: 0 !important;
}
.borderBottomInput:hover {
  border-bottom: 1px solid var(--grey-darker) !important;
}
.row {
  display: flex;
  align-items: center;
  gap: var(--smallSpace);
}
.noPaddingLeft {
  padding-left: 0 !important;
}

/* Tab Navigation */
.tab-navigation {
  display: flex;
  margin-bottom: var(--largeSpace);
  border-bottom: 1px solid var(--border-light);
}

.tab-button {
  padding: var(--space) var(--largeSpace);
  background: transparent;
  border: none;
  color: var(--text-white);
  opacity: 0.6;
  cursor: pointer;
  font-weight: normal;
  transition: opacity 0.3s ease;
  position: relative;
  margin: 0;
}

.tab-button:first-child {
  padding-left: 0;
}

.tab-button:not(:last-child)::after {
  content: "";
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 1.2em;
  width: 1px;
  background-color: var(--border-light);
}

#signed_doc *,
#signed_doc_compact * {
  stroke: var(--icon-stroke-color);
  fill: none;
}
#signed_doc * {
  stroke-width: 11;
}
#signed_doc_compact * {
  stroke-width: 22;
}

.tab-button.active {
  opacity: 0.8;
}

.tab-button:hover {
  opacity: 0.8;
}

/* Content Visibility Table */
.visibility-table {
  min-width: none;
  width: 100%;
  border-collapse: collapse;
  margin-top: var(--space);
}

.user-table th,
.user-table td,
.visibility-table th,
.visibility-table td {
  vertical-align: middle;
}

.user-table tr td:first-child .row {
  display: flex;
  gap: var(--smallSpace);
  justify-content: flex-start;
}

.visibility-table thead th {
  text-align: left;
  border-bottom: 1px solid var(--border-light);
  padding: var(--space) 0;
  font-size: var(--smallFontSize);
}

.visibility-table td {
  text-align: left;
  border-bottom: 1px solid var(--grey-darkest);
  padding: var(--space) 0;
}

.user-table td:last-child,
.visibility-table td:last-child {
  text-align: right;
}
.user-table thead th:last-child,
.visibility-table thead th:last-child {
  text-align: right;
}

.visibility-table select {
  border: 1px solid var(--grey);
  border-radius: var(--border-radius-pill);
  background: var(--bg-main-dark);
  color: var(--text-white);
  padding: var(--smallSpace) var(--space);
  font-size: var(--smallFontSize);
}

.visibility-table input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Delete button row beneath select */
.delete-row {
  margin-top: 0px;
}
.delete-user-button {
  height: auto;
  border: 0px solid var(--grey-darkest);
  color: var(--grey-darkest);
  padding: 0px 0px;
  line-height: 1;
  font-size: var(--smallerFontSize) !important;
}
.delete-user-button:hover {
  color: var(--red);
  background-color: transparent;
}
</style>
